import { computed, type Ref } from 'vue';
import { usePermission } from '@/entities/Permission';
import { DocumentRevisionStatus } from '@/shared/lib';

import type { Homologation } from './homologation.types';
import { useCurrentMemberId } from './useCurrentMemberId';

export function isHomologatableStatus(status: DocumentRevisionStatus | null | undefined): boolean {
  return (
    status === DocumentRevisionStatus.PENDING_APPROVAL ||
    status === DocumentRevisionStatus.PENDING_VERIFICATION
  );
}

export function useHomologationRowPermissions(revision: Ref<Homologation>) {
  const { can } = usePermission();
  const currentMemberId = useCurrentMemberId();

  const canSendDocument = computed(() => can('send', 'Document').value);
  const canManageApproval = computed(() => can('manageApproval', 'Homologation').value);
  const canManageVerification = computed(() => can('manageVerification', 'Homologation').value);

  const currentStatus = computed(() => revision.value.status ?? null);

  const isOwnDocument = computed(
    () =>
      currentMemberId.value !== null &&
      revision.value.document?.memberId !== undefined &&
      Number(revision.value.document.memberId) === Number(currentMemberId.value)
  );

  const isAssignedApproval = computed(() => {
    const managerId = revision.value.approvalManagerId ?? null;
    return (
      currentMemberId.value !== null &&
      managerId !== null &&
      Number(managerId) === Number(currentMemberId.value)
    );
  });

  const canViewHomologation = computed(() => {
    if (canManageVerification.value) return true;
    if (isOwnDocument.value) return true;

    if (currentStatus.value === DocumentRevisionStatus.PENDING_APPROVAL) {
      return canManageApproval.value && isAssignedApproval.value;
    }

    if (currentStatus.value === DocumentRevisionStatus.PENDING_VERIFICATION) {
      return false;
    }

    if (currentStatus.value === DocumentRevisionStatus.VERIFIED) {
      return isAssignedApproval.value;
    }

    return canSendDocument.value || canManageApproval.value;
  });

  const canHomologate = computed(() => {
    if (currentStatus.value === DocumentRevisionStatus.PENDING_APPROVAL) {
      return isAssignedApproval.value && (canManageApproval.value || canManageVerification.value);
    }

    if (currentStatus.value === DocumentRevisionStatus.PENDING_VERIFICATION) {
      return canManageVerification.value;
    }

    return false;
  });

  const canDelete = computed(() => {
    if (currentStatus.value === DocumentRevisionStatus.VERIFIED) {
      return canManageVerification.value;
    }

    return (canSendDocument.value && isOwnDocument.value) || canManageVerification.value;
  });

  const isHomologatable = computed(() => isHomologatableStatus(currentStatus.value));

  return {
    currentStatus,
    canSendDocument,
    canManageApproval,
    canManageVerification,
    isOwnDocument,
    isAssignedApproval,
    canViewHomologation,
    canHomologate,
    canDelete,
    isHomologatable,
  };
}
